import { SUBSCRIPTION_TYPE } from "../constants/v2/Constants";

export function buildPayment(paymentForm: PaymentForm): undefined | PaymentStructure {
    const {
        cardInformation: {
            id: cardId,
            customer: customerId,
            brand,
            lastFourDigits,
            expirationMonth,
            expirationYear
        } = {},
        paypalId,
        paypalEmail
    } = paymentForm;

    if (paypalId && paypalEmail) {
        return {
            paypal: {
                email: paypalEmail,
                baid: paypalId
            }
        };
    }

    if (cardId && customerId && lastFourDigits && expirationMonth && expirationYear) {
        const expMonth = (expirationMonth.toString().length === 1) ? "0".concat(expirationMonth) : expirationMonth;
        const expYear = (expirationYear.toString().length === 2) ? "20".concat(expirationYear) : expirationYear;

        return {
            creditCard: {
                tokenId: cardId,
                secondTokenId: customerId,
                brand,
                lastFourDigits,
                expirationDate: `${ expMonth }${ expYear }`
            }
        };
    }

    return;
}

export function buildSubscription({
    paymentForm,
    billingAddressForm,
    shippingAddressForm,
    subscriptionForm,
    studentForm,
    email,
    promotionType,
    billingPeriod,
    promotionId
}: SubscriptionForm): ZuoraSubscription {
    const {
        selectedProductDetails: {
            productType,
            ratePlanId,
            referralToken,
            promoCode
        }
    } = subscriptionForm as SingleSubscriptionForm;

    const zuoraProviderData: ZuoraData = {
        ratePlanData: {
            ratePlan: {
                productRatePlanId: ratePlanId
            }
        },
        billToAddress: getBillingAddress(billingAddressForm, shippingAddressForm as ShippingAddressForm, email, productType),
        shipToAddress: getShippingAddress(shippingAddressForm as ShippingAddressForm, email),
        paymentMethod: buildPayment(paymentForm),
        studentInfo: getStudentInfo(studentForm),
        billingPeriod: billingPeriod ? billingPeriod.toUpperCase() : null,
        offerType: promotionType
    };

    if (referralToken) {
        zuoraProviderData.referralToken = referralToken;
    }

    if (promotionId) {
        zuoraProviderData.promotionId = promotionId;
    }

    if (promoCode) {
        zuoraProviderData.bloombergPromoCode = promoCode;
    }

    return {
        provider: "ZUORA",
        product: "BBG",
        type: productType,
        source: "WEB",
        providerData: {
            zuora: zuoraProviderData
        }
    };
}

export function getStudentInfo(studentForm: StudentForm): null | StudentInfo {
    const {
        studentId,
        schoolInfo,
        birthDay,
        birthMonth,
        birthYear,
        firstName,
        lastName
    } = studentForm;

    const studentInfo: StudentInfo = {
        studentId,
        organizationId: schoolInfo?.id,
        firstName,
        lastName,
        dateOfBirth: `${ birthYear }-${ birthMonth }-${ birthDay }`
    };

    if (studentInfo.studentId
        && studentInfo.organizationId
        && studentInfo.firstName
        && studentInfo.lastName
        && birthYear
        && birthMonth
        && birthDay) {
        return studentInfo;
    }

    return null;
}

function formatZipCode(postalCode: string): string {
    return postalCode.length > 8 ? postalCode.replace(/-|\s/g, "") : postalCode;
}

export function getShippingAddress(shippingAddressForm: ShippingAddressForm, email: string): null | ShippingAddress {
    const shipToAddress = {
        firstName: shippingAddressForm.firstName,
        lastName: shippingAddressForm.lastName,
        address1: shippingAddressForm.addressLine1,
        address2: shippingAddressForm.addressLine2,
        country: shippingAddressForm.country,
        state: shippingAddressForm.state || "N/A",
        zipCode: shippingAddressForm.postalCode ? formatZipCode(shippingAddressForm.postalCode) : shippingAddressForm.postalCode,
        city: shippingAddressForm.city,
        email
    };

    // [LW] [ENG4SUBS-655] check all required fields exist;
    // otherwise shipToAddress needs to be set to null so that zuora can create soldTo contact correctly
    if (shipToAddress.firstName
        && shipToAddress.lastName
        && shipToAddress.address1
        && shipToAddress.country
        && shipToAddress.city) {
        return shipToAddress as ShippingAddress;
    }

    return null;
}

/*
 * Note that for the all-access flow, we don't ask for billing country/state since we don't want to bog the user down with too
 * many inputs. Thus, we are simply using the shipping address for the billing address to send to zuora since zuora does not
 * have a use for billing address anyway.
 */
export function getBillingAddress(
    billingAddressForm: BillingForm | Record<string, never>,
    shippingAddressForm: ShippingAddressForm | Record<string, never>,
    email: string,
    productType: string
): BillingInfo {
    const isNameMissing = !billingAddressForm.firstName || !billingAddressForm.lastName;

    return {
        firstName: isNameMissing ? "Bloomberg" : billingAddressForm.firstName as string,
        lastName: isNameMissing ? "Subscriber" : billingAddressForm.lastName as string,
        country: productType === SUBSCRIPTION_TYPE.ALL ? shippingAddressForm.country : billingAddressForm.country as string,
        state: (productType === SUBSCRIPTION_TYPE.ALL ? shippingAddressForm.state : billingAddressForm.state) || "N/A",
        zipCode: productType === SUBSCRIPTION_TYPE.ALL ? shippingAddressForm.postalCode : billingAddressForm.postalCode as string,
        email
    };
}

export function buildBulkSubscription({
    paymentForm,
    billingAddressForm,
    bulkSubscriptionForm,
    email
}: SubscriptionForm): ZuoraSubscription {
    const {
        companyName,
        numberOfUsers: numberOfUsersOriginal,
        ratePlan: {
            ratePlanId
        }
    } = bulkSubscriptionForm as BulkSubscriptionForm;

    const numberOfUsers: number
        = typeof numberOfUsersOriginal === "number"
            ? numberOfUsersOriginal : parseInt(numberOfUsersOriginal);

    const {
        firstName,
        lastName,
        addressLine1,
        addressLine2,
        country,
        state,
        postalCode,
        city
    } = billingAddressForm;

    const paymentInfo = buildPayment(paymentForm);

    const address: BillingInfo = {
        firstName,
        lastName,
        address1: addressLine1,
        address2: addressLine2,
        country,
        state: state || "N/A",
        zipCode: postalCode,
        city,
        email
    };

    return {
        provider: "ZUORA",
        product: "BBG",
        type: "BULK",
        source: "WEB",
        providerData: {
            zuora: {
                ratePlanData: {
                    ratePlan: {
                        productRatePlanId: ratePlanId
                    }
                },
                billToAddress: address,
                shipToAddress: null,
                paymentMethod: paymentInfo,
                bulkSubscriptionData: {
                    maxUsersCount: numberOfUsers,
                    name: companyName
                }
            }
        }
    };
}

export default {
    buildPayment,
    buildSubscription,
    buildBulkSubscription
};
